/**
 * Fleet Management > Fuel Cards — the Comdata card mirror.
 *
 * Every shape here is derived from
 * `backend/app/api/api_v1/endpoints/fuel_cards.py` (the list SELECT, the
 * Pydantic request models) and from the `ActionOutcome` dataclass in
 * `backend/app/services/comdata/actions.py`, which the endpoint serialises with
 * `dataclasses.asdict` — so every field of the dataclass is a field of the
 * response, defaults included.
 *
 * The endpoint is deliberately honest about partial success, and this service
 * must not smooth that over:
 * - `meta.mode` is null on a configuration error. Never render it raw; test
 *   `=== 'fixture'` rather than truthiness.
 * - `status_after` is null whenever `ok` is false — the value the service read
 *   before the call may already have been superseded by a sync, so a failure
 *   carries no status at all. Reload the row instead.
 * - `recorded: false` means Corpay may well have acted but our audit/mirror
 *   write did not land. That is not a plain success and not a plain failure.
 * - `skipped: true` means an operator override is in force, so no Corpay call
 *   was made and nothing was written. Render it as a skip, never as an error.
 *   **No route in this service can return it today**, and saying otherwise
 *   unqualified was wrong: `perform_card_action` produces it only when the
 *   source is NOT one of `{"manual", "bulk"}`, and all three write routes here
 *   pass one of those. It is the sweep's outcome, reached through the internal
 *   sweep endpoints. Handling it is kept as a guard, because the cost is a few
 *   lines while the cost of dropping it is that a later widening of the skip
 *   (an admin route, a portal-sourced action) would silently report an honoured
 *   override as a Comdata failure — the exact confusion the ruling exists to
 *   prevent. Treat any page code or test that exercises it as defensive, not as
 *   a description of what these endpoints do.
 * - Bulk answers 200 with per-card `results` even when it stopped early;
 *   `skipped` holds the tokens that were never attempted.
 * - Sync-now can honestly return `records: 0` with a warning saying another run
 *   holds the advisory lock, which is why `warnings` is surfaced.
 *
 * Thrown errors. Everything below is an `AxiosError`, so the page's copy comes
 * from `err.response?.data?.detail`:
 * - **404** — the card is not in the mirror (the detail says "run Sync now").
 *   An unknown token and a never-synced card look identical.
 * - **409** — Comdata writes are switched off, or the integration is disabled /
 *   not configured (sync-now).
 * - **429** — Comdata is rate limiting us; try again in a minute.
 * - **400** — the request was unusable, in practice an unusable `lock_reason`.
 * - **503** — the Comdata client is misconfigured (fixture mode in production).
 * - **403** — RBAC, never Comdata: the token lacks the `Data.ReadWrite` scope, the
 *   caller has no group memberships (fail-closed), or no `api_endpoints` grant
 *   covers this route. Until migration 433 lands, EVERY route here 403s. Render
 *   it as a permissions or not-yet-deployed problem, never as a Comdata fault.
 * `bulk()` is the one method that can also throw a **bare `Error`**; see its doc.
 *
 * Input limits the server enforces, so the page can set `maxLength` instead of
 * surfacing an opaque 422: the list's `q` is capped at 60 characters, and
 * `driverOptions`' `last_four` must be exactly 4. Notes are 255 and a
 * `lock_reason` is 4 (`comdata_status` is `VARCHAR(4)`).
 */
import api from './api';

/**
 * Is one of the mirror's numeric flags set? These columns are `0 | 1` (and
 * `last_action_success` is `0 | 1 | null`), never booleans, so this exists to
 * keep that out of the JSX and to make `1` the only truth — `=== 1`, not
 * `!== 0`, which would read `null` as set.
 *
 * It answers ONE question, deliberately. `!flagSet(x)` does NOT mean "failed":
 * for `last_action_success`, `0` is a recorded failure and `null` is no action
 * ever recorded, and this collapses them exactly as truthiness would. Those two
 * states are what the page exists to tell apart, so test `=== null` for
 * "nothing recorded" first and only then ask whether the flag is set.
 */
export const flagSet = (flag: 0 | 1 | null | undefined): boolean => flag === 1;

/**
 * The server's interactive bulk cap (`MAX_BULK` in fuel_cards.py), exported so
 * the page's selection UI cannot drift from it. `BulkRequest.driver_recnos` is
 * `Field(min_length=1, max_length=MAX_BULK)`, so a longer list is a 422 with no
 * per-card detail at all.
 */
export const MAX_BULK = 25;

/** Comdata's card status: 'A' active, anything else (or null) is not active. */
export type ComdataStatus = string | null;

/**
 * One row of `GET /fuel-cards` — every column the list projection selects, in
 * its order. `raw_json` is deliberately absent from the projection.
 *
 * The driver and unit columns come from LEFT JOINs, so they are null on an
 * unlinked card (and `unit_status` is null on a linked card whose unit row
 * cannot be resolved — the "unjudged" case the sweep cannot decide).
 */
export interface FuelCardRow {
  /** The key. One row per driver holding a fuel card. */
  driver_recno: number;
  /** Display only — the full number stays in V1 and is never sent here. */
  last_four: string | null;
  /** Which Comdata customer id this card answers under (34287 / 16808). */
  customer_id: string | null;
  /** From cardInquiryV04. Metadata; nothing addresses a card by it. */
  card_token: string | null;
  card_first_name: string | null;
  card_last_name: string | null;
  employee_number: string | null;
  /** `comdata_card.unit_number` — the unit Comdata has on the card. */
  card_unit_number: string | null;
  comdata_status: ComdataStatus;
  /** Raw string: the service mixes date formats. */
  comdata_status_date: string | null;
  card_balance: string | null;
  /** 1 = a manual block/unblock is in force and the sweep skips this card. */
  override_flag: 0 | 1;
  override_by: string | null;
  override_at: string | null;
  override_reason: string | null;
  last_sweep_action: 'block' | 'unblock' | null;
  /** >= 1 is the endpoint's whole Needs-attention rule (a timed-out block stops at 1). */
  consecutive_failures: number;
  last_error: string | null;
  last_synced_at: string | null;
  driver_firstname: string | null;
  driver_lastname: string | null;
  driver_unitnumber: string | null;
  driver_fueltype: string | null;
  /** V1 unit status; null means no unit row resolved for this driver. */
  unit_status: string | null;
  last_action: string | null;
  last_action_source: string | null;
  last_action_by: string | null;
  last_action_at: string | null;
  last_action_success: 0 | 1 | null;
}


/**
 * Banner state. The endpoint never lets a configuration error become a 500, so
 * a misconfigured box reads as `configured: false` with the reason in
 * `writes_disabled_reason` and a **null** `mode`.
 */
export interface FuelCardsMeta {
  writes_enabled: boolean;
  /** Null exactly when writes are enabled. */
  writes_disabled_reason: string | null;
  /** Null on a configuration error — do not render raw. */
  mode: 'live' | 'fixture' | null;
  configured: boolean;
  /** The COMDATA_ENABLED switch, independent of whether credentials are present. */
  enabled: boolean;
  /**
   * Cards Comdata reports no longer exist — hidden from every list, so this
   * count is the only thing explaining why the page shows far fewer rows than
   * there are card numbers on driver records.
   */
  retired_count: number;
  last_synced_at: string | null;
}

export interface FuelCardListFilters {
  status?: 'all' | 'active' | 'blocked';
  override?: boolean;
  /** `consecutive_failures >= 1`. Lifts the terminated-card hide. */
  attention?: boolean;
  /** Linked, but the driver's unit row cannot be resolved. Lifts the hide too. */
  unjudged?: boolean;
  /** Shows terminated-plus-blocked cards, which the default view tidies away. */
  include_terminated?: boolean;
  q?: string;
}

export interface FuelCardsListResponse {
  data: FuelCardRow[];
  meta: FuelCardsMeta;
}

/**
 * The `ActionOutcome` dataclass, verbatim. Nothing here is optional: the
 * endpoint uses `asdict`, so the fields with dataclass defaults are present on
 * the wire too.
 */
export interface ActionOutcome {
  ok: boolean;
  action: 'block' | 'unblock';
  driver_recno: number;
  /** Null on ANY failure — the endpoint nulls it rather than echo a stale mirror read. */
  status_after: string | null;
  response_code: string | null;
  response_message: string | null;
  correlation_id: string | null;
  error: string | null;
  http_status: number | null;
  /**
   * No Corpay call was made and nothing was written (an override is in force).
   * Always `false` from every route in this service — the skip fires only for a
   * non-manual source, i.e. the sweep. Kept in the type because it is the same
   * `ActionOutcome` the sweep builds, and handled on the page as a guard against
   * a later widening. See the module doc.
   */
  skipped: boolean;
  /** The audit row and mirror write landed. False = "refresh after the next sync". */
  recorded: boolean;
}

export interface BulkResult {
  /** One entry per card actually attempted, in request order. */
  results: ActionOutcome[];
  stopped_early: boolean;
  /**
   * Human prose for display only. It is explicitly NOT a stable identifier —
   * nothing may branch on its text. Branch on `stopped_early` and `results`.
   */
  stop_reason: string | null;
  /** Tokens that were never attempted. Empty on a complete run. */
  skipped: number[];
}

export interface BlockReason {
  blockCode: string;
  blockDescription: string;
}

export interface BlockReasonsResponse {
  data: BlockReason[];
  /** 'comdata' = Corpay's live list; 'fallback' = the static two-entry list. */
  source: 'comdata' | 'fallback';
}

/**
 * A non-terminated V1 driver with a card on file, for the Link picker.
 *
 * `firstname`, `lastname`, `unitnumber` and `fueltype` are `NOT NULL DEFAULT ''`
 * in `safetynew.driver` (verified read-only: 0 of 6,158 rows null in any of
 * them), and `last_four` is `RIGHT(TRIM(d.fuelcardnumber), 4)` over another
 * `NOT NULL` column that the query already guards with `TRIM(...) <> ''`. So
 * the "nothing on file" sentinel is the EMPTY STRING, not null — a `?? ''`
 * guard is a no-op here and a nameless driver would render as ", ". Guard with
 * `.trim()` instead.
 *
 * `unit_status` stays nullable: `unit.status` is `NOT NULL` but is reached
 * through a LEFT JOIN, so null genuinely means "no unit row" — the unjudged case.
 */
/** `GET /fuel-cards/drivers`. `truncated` means the cap was hit, not that the
 *  search found everything there is. */
export interface CardActionRow {
  id: number;
  /** block | unblock | observed_change | link | unlink | override_clear */
  action: string;
  /** manual | bulk | sweep | sync */
  source: string;
  requested_by: string;
  lock_reason: string | null;
  note: string | null;
  correlation_id: string | null;
  http_status: number | null;
  response_code: string | null;
  response_message: string | null;
  success: 0 | 1;
  error: string | null;
  duration_ms: number | null;
  status_before: string | null;
  status_after: string | null;
  created_at: string;
}

export interface CardActionsResponse {
  data: CardActionRow[];
  /** The card has more history than was returned — say so, never clip silently. */
  truncated: boolean;
  limit: number;
}

/** `{ok, action, driver_recno}` from link / unlink / override-clear. */
export interface CardMutationResult {
  ok: boolean;
  action: 'override_clear';
  driver_recno: number;
}


/** `run_card_sync`'s summary — the same dict the scheduler logs. */
export interface CardSyncSummary {
  records: number;
  inserted: number;
  updated: number;
  status_changed: number;
  errors: number;
  api_calls: number;
  api_time_ms: number;
  skipped: number;
  /** Mirrored cards Comdata did not return this run. Reported only. */
  stale: number;
  /**
   * Why a run did less than it looks like it should have — "another card sync
   * is already running; skipped" is a 200 with zero records. Always surface it.
   */
  warnings: string[];
}

export interface BulkRequest {
  driver_recnos: number[];
  action: 'block' | 'unblock';
  lock_reason?: string;
  note?: string;
}

class FuelCardsService {
  /** The card mirror plus the banner state. Unset filters are not sent. */
  async list(filters: FuelCardListFilters = {}): Promise<FuelCardsListResponse> {
    const params: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(filters)) {
      // `false` is a real filter value (linked=false is "unlinked"), so only
      // undefined/null/'' are dropped — and dropped by omitting the key, not by
      // assigning undefined, which would leave a dead key on the params object.
      if (value !== undefined && value !== null && value !== '') params[key] = value;
    }
    const res = await api.get('/fuel-cards', { params });
    return res.data;
  }

  async block(driverRecno: number, body: { lock_reason?: string; note?: string } = {}): Promise<ActionOutcome> {
    const res = await api.post(`/fuel-cards/${driverRecno}/block`, body);
    return res.data;
  }

  async unblock(driverRecno: number, body: { note?: string } = {}): Promise<ActionOutcome> {
    const res = await api.post(`/fuel-cards/${driverRecno}/unblock`, body);
    return res.data;
  }

  /**
   * Block or unblock up to {@link MAX_BULK} cards in one request.
   *
   * Over the cap this REJECTS locally rather than chunking. Chunking would turn
   * one honest envelope into several and then have to invent a merged one: a
   * request-level 409/422/503 on chunk 2 arrives as a thrown error with no
   * record of chunk 1's completed cards, and `stopped_early` / `stop_reason`
   * are per-request facts that cannot be summed. The server also stops starting
   * new cards at its own deadline, so a chunked run could stack several
   * minutes-long requests. Rejecting keeps the contract "one call, one
   * envelope" and leaves the capping to the selection UI, which knows what the
   * operator picked. The empty case is rejected for the same reason: the
   * server's `min_length=1` would answer 422 with nothing per-card to show.
   *
   * Tokens are de-duped here, before the cap is checked, so that
   * `results.length + skipped.length === <the tokens sent>` holds
   * unconditionally. The server de-dupes too, and a caller comparing against
   * its own raw selection would read a COMPLETE run as partial — a false
   * "partial run" banner is exactly the kind of dishonesty the per-card
   * envelope exists to prevent. Checking the cap after the de-dupe is therefore
   * both correct (it is the number actually sent) and strictly more permissive:
   * 26 tokens containing one repeat is a legal run of 25.
   *
   * These two rejections are a **bare `Error`**, not an `AxiosError` — the only
   * place in this service where that is true. A `catch` written for the usual
   * shape must read `err.response?.data?.detail ?? err.message`, or it renders
   * nothing at all for an over-cap or empty selection.
   */
  async bulk(body: BulkRequest): Promise<BulkResult> {
    const driver_recnos = [...new Set(body.driver_recnos)];
    if (driver_recnos.length === 0) {
      throw new Error('Select at least one card.');
    }
    if (driver_recnos.length > MAX_BULK) {
      throw new Error(
        `Bulk actions run at most ${MAX_BULK} cards at a time (${driver_recnos.length} selected). ` +
          'Narrow the selection and repeat for the rest.',
      );
    }
    const res = await api.post('/fuel-cards/bulk', { ...body, driver_recnos });
    return res.data;
  }

  async clearOverride(driverRecno: number): Promise<CardMutationResult> {
    const res = await api.post(`/fuel-cards/${driverRecno}/override/clear`, {});
    return res.data;
  }

  /** Run the card sync on demand. Show `warnings` — a zero-record run is often lock contention, not an empty mirror. */
  async syncNow(): Promise<CardSyncSummary> {
    const res = await api.post('/fuel-cards/sync', {});
    return res.data;
  }

  /** The block-reason picker, with the `source` that says live list vs static fallback. */
  async blockReasons(): Promise<BlockReasonsResponse> {
    const res = await api.get('/fuel-cards/block-reasons');
    return res.data;
  }

  /**
   * Drivers for the Link picker.
   *
   * `search` goes to the SERVER. The list is longer than the row cap, so filtering
   * a returned page searches the first N rows rather than the driver table, and a
   * driver past the cap cannot be found however carefully anyone types. The
   * response's `truncated` is what lets the picker say "narrow your search"
   * instead of implying the driver does not exist.
   */
  /** Newest-first audit trail. Returns the envelope so `truncated` cannot be dropped on the floor. */
  async actions(driverRecno: number): Promise<CardActionsResponse> {
    const res = await api.get(`/fuel-cards/${driverRecno}/actions`);
    return res.data;
  }
}

export const fuelCardsService = new FuelCardsService();
export default fuelCardsService;
